import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetGrupoAnalises } from '../../../../../../../../../services/AgriculturaPrecisao/NewOSColeta/GET'
import { destroyAlert, errorAlert, infoAlert, loadingAlert } from '../../../../../../../../../utils/alert'
import {
  analisesSalvasParaCards,
  aplicarMalhaTalhao,
  malhaSalvaParaPontos,
  parseShapefile,
} from '../components/analises/meta'
import { reprecificarAnalises } from '../meta/reprecificarAnalises'
import type { AnaliseAdicionada, GeoPointFeature } from '../types'
import type { OsItensTypes } from '../../../../../../../../../services/OsParaColeta/GET'

interface UseAnalisesParams {
  /** Laboratório selecionado no formulário (dispara a busca do grupo de análises). */
  cod_laboratorio: number | null
  /** Data da OS, usada como data de vigência das análises do laboratório. */
  data_vigencia?: string
}

/** Converte "R$ 1.234,56" (ou número) em Number. */
function parseValorBRL(valor?: string): number {
  if (!valor) return 0
  const limpo = String(valor).replace(/[^\d,-]/g, '').replace(',', '.')
  const n = parseFloat(limpo)
  return isNaN(n) || !isFinite(n) ? 0 : n
}

/**
 * Estado e ações da metade "Análises" do formulário de OS.
 * Concentra malha/pontos/análises/totais e é exposto no FormOsContext sob `analises`.
 */
export function useAnalises({ cod_laboratorio, data_vigencia }: UseAnalisesParams) {
  //#region ESTADO
  const [malha, setMalha] = useState<GeoPointFeature[]>([])
  const [malhaShp, setMalhaShp] = useState<any>([])
  const [pontosTotalMalha, setPontosTotalMalha] = useState<number>(0)
  const [loadingShape, setLoadingShape] = useState<boolean>(false)
  /**
   * Coleções de um zip multi-talhão aguardando escolha do usuário. A OS aceita um
   * único talhão, então o arquivo não é aplicado até ele decidir qual importar.
   */
  const [colecoesMalha, setColecoesMalha] = useState<any[]>([])
  const [colecaoSelecionada, setColecaoSelecionada] = useState<number | null>(null)
  const [abrirSelecaoTalhaoMalha, setAbrirSelecaoTalhaoMalha] = useState<boolean>(false)

  const [talhaoKml, setTalhaoKml] = useState<any[]>([])
  const [talhaoArea, setTalhaoArea] = useState<string>('')
  /**
   * O contorno foi editado MANUALMENTE nesta sessão (mapa de "Editar contorno").
   * `talhaoKml` também é preenchido pela hidratação da OS e pela sincronização do
   * talhão selecionado — enviar o KML nesses casos reescreveria o contorno com o
   * mesmo valor a cada salvar. Só o que o usuário desenhou vai no payload.
   */
  const [contornoEditado, setContornoEditado] = useState<boolean>(false)
  const [analises, setAnalises] = useState<AnaliseAdicionada[]>([])

  /**
   * Assinatura das análises no estado "limpo" (vazio ao criar; a OS carregada
   * ao editar/copiar). Comparada com a assinatura atual para saber se o usuário
   * mexeu na metade "Análises" — ver `analisesDirty`.
   */
  const [assinaturaBase, setAssinaturaBase] = useState<string>('[]')

  // Seleção em curso (usada pelo modal de nova análise — sub-etapa futura)
  const [abrirModalNova, setAbrirModalNova] = useState<boolean>(false)
  const [id_em_edicao, setId_em_edicao] = useState<string | number | null>(null)
  const [cor, setCor] = useState<string>('')
  const [tipo_analise_selecionada, setTipo_analise_selecionada] = useState<string>('')
  const [tipo_analise_selecionada_id, setTipo_analise_selecionada_id] = useState<number | null>(null)
  const [pontos_selecionados, setPontos_selecionados] = useState<GeoPointFeature[]>([])
  const [pontos_selecionados_adicionados, setPontos_selecionados_adicionados] = useState<any[]>([])

  //#region GRUPO DE ANÁLISES (do laboratório)
  const { data_grupo_analises, carregando_grupo_analises } = useGetGrupoAnalises(
    cod_laboratorio,
    data_vigencia
  )

  //#region TROCA DE LABORATÓRIO
  /**
   * Laboratório ao qual os preços das análises atuais pertencem. Começa nulo e é
   * fixado na hidratação (editar/copiar) ou na primeira seleção; comparar com
   * `cod_laboratorio` é o que detecta a troca.
   */
  const labDasAnalises = useRef<number | null>(null)

  /** Análises que o novo laboratório não oferece — alimentam o modal de confirmação. */
  const [analisesNaoEncontradas, setAnalisesNaoEncontradas] = useState<AnaliseAdicionada[]>([])
  /** Análises já reprecificadas, aplicadas se o usuário optar por prosseguir. */
  const [analisesReprecificadas, setAnalisesReprecificadas] = useState<AnaliseAdicionada[]>([])
  /** Laboratório anterior, para reverter caso o usuário cancele a troca. */
  const [labAnteriorTroca, setLabAnteriorTroca] = useState<number | null>(null)
  const [abrirConfirmarTrocaLab, setAbrirConfirmarTrocaLab] = useState<boolean>(false)

  /**
   * Ao trocar de laboratório com análises já adicionadas, os preços precisam ser
   * recalculados contra a tabela do novo lab — senão a OS é salva com os valores
   * do laboratório antigo, silenciosamente. Espelha `handelChangeLaboratorio` da
   * versão em produção.
   */
  useEffect(() => {
    // Sem laboratório ou com o grupo ainda carregando não há o que comparar.
    if (!cod_laboratorio || carregando_grupo_analises) return

    const labAnterior = labDasAnalises.current

    // Primeira definição do laboratório (criar ou hidratar): só registra.
    if (labAnterior === null) {
      labDasAnalises.current = cod_laboratorio
      return
    }

    if (labAnterior === cod_laboratorio) return

    // Trocou de laboratório. Sem análises adicionadas não há preço a recalcular.
    if (analises.length === 0) {
      labDasAnalises.current = cod_laboratorio
      return
    }

    const grupo = data_grupo_analises ?? []

    if (grupo.length === 0) {
      infoAlert('Laboratório selecionado não possui análises cadastradas!')
    }

    const { encontradas, nao_encontradas } = reprecificarAnalises(analises, grupo)

    if (nao_encontradas.length > 0) {
      // Descartar análises é decisão do usuário — abre o modal e só aplica depois.
      setAnalisesNaoEncontradas(nao_encontradas)
      setAnalisesReprecificadas(encontradas)
      setLabAnteriorTroca(labAnterior)
      setAbrirConfirmarTrocaLab(true)
      return
    }

    // Todas as análises existem no novo laboratório: aplica os novos preços direto.
    setAnalises(encontradas)
    labDasAnalises.current = cod_laboratorio
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cod_laboratorio, data_grupo_analises, carregando_grupo_analises])

  /** Mantém a troca, descartando as análises que o novo laboratório não tem. */
  const confirmarTrocaLaboratorio = useCallback(() => {
    setAnalises(analisesReprecificadas)
    labDasAnalises.current = cod_laboratorio
    setAbrirConfirmarTrocaLab(false)
    setAnalisesNaoEncontradas([])
    setAnalisesReprecificadas([])
    setLabAnteriorTroca(null)
  }, [analisesReprecificadas, cod_laboratorio])

  /**
   * Desfaz a troca. Devolve o cod do laboratório anterior para o formulário
   * restaurar o Select — as análises permanecem intactas.
   */
  const cancelarTrocaLaboratorio = useCallback((): number | null => {
    const voltarPara = labAnteriorTroca
    labDasAnalises.current = voltarPara
    setAbrirConfirmarTrocaLab(false)
    setAnalisesNaoEncontradas([])
    setAnalisesReprecificadas([])
    setLabAnteriorTroca(null)
    return voltarPara
  }, [labAnteriorTroca])

  /** Grupo com o rótulo composto "(profundidade) | análise" usado nos selects. */
  const grupoAnalisesTratado = useMemo(
    () =>
      (data_grupo_analises ?? []).map((item) => ({
        ...item,
        analise_profundidade_nome: `(${item?.profundidade_nome}) | ${item?.analise_nome}`,
      })),
    [data_grupo_analises]
  )

  //#region DIRTY
  /**
   * Resumo estável do conteúdo das análises (o que o usuário pode alterar:
   * quais análises existem, de que tipo e com quantos pontos). Evita comparar
   * os objetos inteiros, que carregam geometria e mudam de referência à toa.
   */
  const assinaturaAnalises = useCallback(
    (lista: AnaliseAdicionada[]) =>
      JSON.stringify(
        (lista ?? []).map((a: any) => [a?.id, a?.cod_analises?.cod, a?.pontos?.length ?? 0])
      ),
    []
  )

  const assinaturaAtual = useMemo(() => assinaturaAnalises(analises), [analises, assinaturaAnalises])

  /**
   * O usuário mexeu na metade "Análises"? Inclui o contorno: editá-lo é uma
   * alteração pendente como qualquer outra, e sem isso fechar a gaveta logo após
   * editar o contorno não pediria confirmação.
   */
  const analisesDirty = assinaturaAtual !== assinaturaBase || contornoEditado

  //#region AÇÕES
  const resetAnalises = useCallback(() => {
    setMalha([])
    setMalhaShp([])
    setPontosTotalMalha(0)
    setTalhaoKml([])
    setContornoEditado(false)
    setTalhaoArea('')
    setAnalises([])
    setColecoesMalha([])
    setColecaoSelecionada(null)
    setAbrirSelecaoTalhaoMalha(false)
    // Zera o rastreio de laboratório: a próxima seleção é uma definição, não troca.
    labDasAnalises.current = null
    setAbrirConfirmarTrocaLab(false)
    setAnalisesNaoEncontradas([])
    setAnalisesReprecificadas([])
    setLabAnteriorTroca(null)
    setAbrirModalNova(false)
    setId_em_edicao(null)
    setCor('')
    setTipo_analise_selecionada('')
    setTipo_analise_selecionada_id(null)
    setPontos_selecionados([])
    setPontos_selecionados_adicionados([])
    setAssinaturaBase('[]')
  }, [])

  /**
   * Pré-preenche a metade "Análises" a partir de uma OS carregada (editar/copiar).
   * Faz as transformações de malha/análises uma única vez e aplica em poucos setStates.
   */
  const hidratarAnalises = useCallback((os: OsItensTypes) => {
    if (!os) return
    setMalhaShp(os.malha_de_pontos ?? [])
    setMalha(malhaSalvaParaPontos(os.malha_de_pontos))
    setPontosTotalMalha(os.malha_de_pontos?.features?.length ?? 0)
    // Contorno vem como {latitude, longitude} (string); o mapa exige {lat, lng} numérico.
    const contorno = (os.talhao_kml?.coordenadas ?? [])
      .map((c: any) => ({ lat: Number(c?.latitude), lng: Number(c?.longitude) }))
      .filter((c) => !isNaN(c.lat) && !isNaN(c.lng))
    setTalhaoKml(contorno as any)
    // Contorno vindo do backend: ainda não é edição do usuário.
    setContornoEditado(false)
    // A área vem pronta na própria OS. Não dá para esperar apenas o talhão
    // selecionado resolver a área: a lista de talhões é outra requisição e ainda
    // filtra subdivisões (cod_talhao !== null), então a OS pode apontar para um
    // talhão fora da lista e a área ficaria em '0 ha'.
    if (os.talhao_area) setTalhaoArea(String(os.talhao_area))
    const analisesDaOs = analisesSalvasParaCards(os.analises as any)
    setAnalises(analisesDaOs)
    // A OS carregada é a baseline: hidratar não é alteração do usuário.
    setAssinaturaBase(assinaturaAnalises(analisesDaOs))
  }, [assinaturaAnalises])

  /**
   * Aplica o contorno desenhado pelo usuário no mapa de "Editar contorno".
   * Marca `contornoEditado` para que o KML seja incluído no payload do salvar —
   * é o que faz a edição persistir (antes ficava só no estado da gaveta).
   */
  const salvarContornoEditado = useCallback((coordenadas: { lat: number; lng: number }[]) => {
    setTalhaoKml(coordenadas as any)
    setContornoEditado(true)
  }, [])

  /** Aplica uma coleção já parseada como malha da OS. */
  const aplicarColecaoNaMalha = useCallback((colecao: any) => {
    const resultado = aplicarMalhaTalhao(colecao)
    setMalhaShp(resultado.malhaShp)
    setMalha(resultado.malha)
    setPontosTotalMalha(resultado.total)
  }, [])

  /** Importa um shapefile (.shp/.zip) e substitui a malha atual. */
  const importarMalha = useCallback(
    async (files: File[]) => {
      if (!files || files.length === 0) return
      try {
        setLoadingShape(true)
        loadingAlert({ msg: 'Processando malha...', id: 'import_malha' })

        const { malha: resultado, colecoes } = await parseShapefile(files)

        // Zip com vários talhões: a OS aceita um só, o usuário escolhe qual.
        if (colecoes.length > 1) {
          const ordenadas = [...colecoes].sort((a, b) =>
            String(a?.fileName ?? '').localeCompare(String(b?.fileName ?? ''))
          )
          setColecoesMalha(ordenadas)
          setColecaoSelecionada(null)
          setAbrirSelecaoTalhaoMalha(true)
          destroyAlert('import_malha')
          return
        }

        if (resultado) {
          setMalhaShp(resultado.malhaShp)
          setMalha(resultado.malha)
          setPontosTotalMalha(resultado.total)
        }
        destroyAlert('import_malha')
      } catch (error) {
        setMalhaShp([])
        errorAlert(
          'Arquivo inválido. Verifique o shapefile e tente novamente.',
          5000,
          'import_malha'
        )
      } finally {
        setLoadingShape(false)
      }
    },
    []
  )

  /** Confirma o talhão escolhido no modal de seleção e aplica a malha dele. */
  const confirmarTalhaoDaMalha = useCallback(() => {
    if (colecaoSelecionada === null) {
      infoAlert('Selecione o talhão!')
      return
    }

    try {
      aplicarColecaoNaMalha(colecoesMalha[colecaoSelecionada])
      setAbrirSelecaoTalhaoMalha(false)
      setColecoesMalha([])
      setColecaoSelecionada(null)
    } catch (error) {
      setMalhaShp([])
      errorAlert('Arquivo inválido. Verifique o shapefile e tente novamente.', 5000)
    }
  }, [colecaoSelecionada, colecoesMalha, aplicarColecaoNaMalha])

  /** Cancela a seleção de talhão: nenhuma malha é importada. */
  const cancelarTalhaoDaMalha = useCallback(() => {
    setAbrirSelecaoTalhaoMalha(false)
    setColecoesMalha([])
    setColecaoSelecionada(null)
  }, [])

  /**
   * Aplica o arraste de um ponto feito no mapa do modal de análise.
   *
   * O mesmo ponto vive em quatro lugares: na malha (`malha`, usado pela UI, e
   * `malhaShp`, que é o que vai no payload como `malha_de_pontos`) e dentro de cada
   * análise que o usa — a análise guarda uma CÓPIA da coordenada, e é essa cópia que
   * o payload envia. Atualizar só o mapa deixava o arraste sem efeito: a malha voltava
   * ao reabrir e as análises seguiam apontando para a posição antiga.
   *
   * O ponto é localizado por `NOME`, a identidade usada em todo o módulo.
   */
  const moverPontoDaMalha = useCallback(
    (nome: number | string, latitude: number, longitude: number) => {
      const mesmoPonto = (ponto: GeoPointFeature) => String(ponto?.NOME) === String(nome)

      const reposicionar = (pontos: GeoPointFeature[]) =>
        (pontos ?? []).map((ponto) =>
          mesmoPonto(ponto) ? { ...ponto, LATITUDE: latitude, LONGITUDE: longitude } : ponto
        )

      setMalha(reposicionar)

      setMalhaShp((atual: any) => ({
        ...atual,
        features: (atual?.features ?? []).map((feature: any) =>
          String(feature?.properties?.NOME) === String(nome)
            ? {
                ...feature,
                geometry: {
                  ...feature?.geometry,
                  coordinates: [String(longitude), String(latitude)],
                },
                properties: {
                  ...feature?.properties,
                  LATITUDE: String(latitude),
                  LONGITUDE: String(longitude),
                },
              }
            : feature
        ),
      }))

      setAnalises((atual) =>
        atual.map((analise) => ({
          ...analise,
          pontos_selecionados: reposicionar(analise.pontos_selecionados),
        }))
      )

      // Grupos já montados no modal, ainda sem passar pelo salvar, guardam a mesma cópia.
      setPontos_selecionados_adicionados((atual) =>
        (atual ?? []).map((grupo: any) => ({
          ...grupo,
          pontos_selecionados: reposicionar(grupo?.pontos_selecionados),
        }))
      )
    },
    []
  )

  /**
   * Insere na malha um ponto criado no mapa do modal de análise.
   *
   * A numeração sai do maior `NOME` em uso, e não da quantidade de pontos: numa malha
   * cujos nomes não são 1..N — importada de shapefile, por exemplo — contar os pontos
   * produz um nome repetido, e `NOME` é justamente a identidade que liga o ponto às
   * análises.
   *
   * Devolve o ponto criado para o mapa desenhá-lo com o mesmo número que foi gravado.
   */
  const adicionarPontoNaMalha = useCallback(
    (latitude: number, longitude: number): GeoPointFeature => {
      const maiorNome = malha.reduce((maior, ponto) => {
        const numero = Number(ponto?.NOME)
        return Number.isFinite(numero) && numero > maior ? numero : maior
      }, 0)

      const numero = maiorNome + 1

      const novoPonto: GeoPointFeature = {
        INDICE: numero,
        NOME: numero,
        LATITUDE: latitude,
        LONGITUDE: longitude,
        COR: null,
      }

      setMalha((atual) => [...atual, novoPonto])
      setPontosTotalMalha((atual) => atual + 1)

      setMalhaShp((atual: any) => ({
        ...atual,
        features: [
          ...(atual?.features ?? []),
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [String(longitude), String(latitude)],
            },
            properties: {
              INDICE: String(numero),
              NOME: String(numero),
              COMENTARIO: null,
              ICONE: null,
              ALTITUDE: null,
              DATA: null,
              ANGULO: null,
              DISPLAY: null,
              ZONA: null,
              LATITUDE: String(latitude),
              LONGITUDE: String(longitude),
            },
          },
        ],
      }))

      return novoPonto
    },
    [malha]
  )

  const deletarAnalise = useCallback((analiseId: string | number) => {
    setAnalises((prev) => prev.filter((a) => a?.id !== analiseId))
  }, [])

  const removerPonto = useCallback((analiseId: string | number, pontoNome: number | string) => {
    setAnalises((prev) =>
      prev.map((analise) => {
        if (analise.id !== analiseId) return analise
        const novosPontos = analise.pontos_selecionados.filter((p) => p.NOME !== pontoNome)
        const valorUnitario = Number(analise.valor) || 0
        const novoValor = (valorUnitario * novosPontos.length).toLocaleString('pt-br', {
          style: 'currency',
          currency: 'BRL',
        })
        return { ...analise, pontos_selecionados: novosPontos, valor_analise: novoValor }
      })
    )
  }, [])

  /** Fecha o modal de nova análise e limpa a seleção em curso. */
  const fecharModalNova = useCallback(() => {
    setId_em_edicao(null)
    setCor('')
    setTipo_analise_selecionada('')
    setTipo_analise_selecionada_id(null)
    setPontos_selecionados([])
    setPontos_selecionados_adicionados([])
    setAbrirModalNova(false)
  }, [])

  /**
   * Persiste a seleção do modal de nova análise na lista de análises.
   * Se está editando (id_em_edicao), substitui o card; senão, adiciona.
   */
  const salvarPontosSelecionados = useCallback(() => {
    const dadosFormatados: AnaliseAdicionada[] = (pontos_selecionados_adicionados ?? []).map(
      (item: any) => ({
        id: item?.id || Date.now(),
        cod_analise: item?.cod_analise,
        pontos_selecionados: (item?.pontos_selecionados ?? []).map((p: GeoPointFeature) => ({
          ...p,
          COR: p.COR || item?.cor,
        })),
        profundidade: item?.profundidade,
        analise_nome: item?.analise_nome,
        valor: item?.valor,
        cor: item?.cor,
        valor_analise: item?.valor_analise,
      })
    )

    if (id_em_edicao) {
      setAnalises((prev) => prev.map((a) => (a.id === id_em_edicao ? dadosFormatados[0] : a)))
    } else {
      setAnalises((prev) => [...prev, ...dadosFormatados])
    }

    fecharModalNova()
  }, [pontos_selecionados_adicionados, id_em_edicao, fecharModalNova])

  /** Abre o modal de nova análise já com os pontos/análise atuais (edição de um card). */
  const inserirPontosSelecionados = useCallback(
    (pontos: GeoPointFeature[], id: string | number, cod_analise: number) => {
      const filtro = grupoAnalisesTratado.find((item) => item?.cod_analise === cod_analise)
      setTipo_analise_selecionada(filtro?.analise_profundidade_nome ?? '')
      setTipo_analise_selecionada_id(filtro?.cod_analise ?? null)
      setCor(filtro?.cor ?? '')
      setId_em_edicao(id)
      setPontos_selecionados(pontos)
      setAbrirModalNova(true)
    },
    [grupoAnalisesTratado]
  )

  //#region TOTAIS DERIVADOS
  const total_amostras = useMemo(
    () => analises.reduce((acc, item) => acc + (item.pontos_selecionados?.length || 0), 0),
    [analises]
  )

  const grid_amostral = useMemo(() => {
    if (pontosTotalMalha > 0) {
      return (parseFloat(talhaoArea) / pontosTotalMalha).toFixed(2)
    }
    return '0.00'
  }, [talhaoArea, pontosTotalMalha])

  const valor_total = useMemo(
    () => analises.reduce((acc, item) => acc + parseValorBRL(item.valor_analise), 0),
    [analises]
  )

  const rs_por_ha = useMemo(() => {
    const area = parseFloat(talhaoArea)
    if (!valor_total || !area || area <= 0 || !isFinite(area)) return 0
    return valor_total / area
  }, [valor_total, talhaoArea])

  return {
    // estado bruto
    malha,
    setMalha,
    malhaShp,
    setMalhaShp,
    pontosTotalMalha,
    setPontosTotalMalha,
    loadingShape,
    talhaoKml,
    setTalhaoKml,
    contornoEditado,
    salvarContornoEditado,
    talhaoArea,
    setTalhaoArea,
    analises,
    setAnalises,

    // seleção em curso (para modal de nova análise)
    abrirModalNova,
    setAbrirModalNova,
    id_em_edicao,
    setId_em_edicao,
    cor,
    setCor,
    tipo_analise_selecionada,
    setTipo_analise_selecionada,
    tipo_analise_selecionada_id,
    setTipo_analise_selecionada_id,
    pontos_selecionados,
    setPontos_selecionados,
    pontos_selecionados_adicionados,
    setPontos_selecionados_adicionados,

    // grupo de análises
    grupoAnalises: data_grupo_analises,
    grupoAnalisesTratado,
    carregando_grupo_analises,

    // dirty
    analisesDirty,

    // seleção de talhão em zip multi-talhão
    abrirSelecaoTalhaoMalha,
    colecoesMalha,
    colecaoSelecionada,
    setColecaoSelecionada,
    confirmarTalhaoDaMalha,
    cancelarTalhaoDaMalha,

    // troca de laboratório (reprecificação das análises)
    abrirConfirmarTrocaLab,
    analisesNaoEncontradas,
    confirmarTrocaLaboratorio,
    cancelarTrocaLaboratorio,

    // ações
    resetAnalises,
    hidratarAnalises,
    importarMalha,
    moverPontoDaMalha,
    adicionarPontoNaMalha,
    deletarAnalise,
    removerPonto,
    inserirPontosSelecionados,
    salvarPontosSelecionados,
    fecharModalNova,

    // totais
    total_amostras,
    grid_amostral,
    valor_total,
    rs_por_ha,
  }
}
